import { ref } from "vue";
import * as utils from "@utils/index";
import zhCN from "./zh-CN";
import zhTW from "./zh-TW";
import enUS from "./en-US";
import idID from "./id-ID";
import thTH from "./th-TH";

const languageList = ["zh-CN", "en-US", "id-ID", "th-TH"];

const languageTxt = {
  "zh-CN": zhCN,
  "en-US": enUS,
  "id-ID": idID,
  "th-TH": thTH,
};

const LANG_STORAGE_KEY = "rowsea_cocreation_lang";

const _checkLang = (lang: string): string => {
  if (!lang) return "";
  if (languageList.includes(lang)) return lang;
  // 支持短码模糊匹配，如 "th" 匹配 "th-TH"
  const reg = new RegExp(lang, "i");
  return languageList.find((l) => reg.test(l)) || "";
};

const _getSystemLang = (): string => {
  const raw: string =
    (navigator as any).language ||
    (navigator as any).userLanguage ||
    (navigator as any).browserLanguage ||
    (navigator as any).systemLanguage ||
    "";
  return _checkLang(raw);
};

const _resolveLang = (): string => {
  const fromUrl =
    _checkLang(utils.getUrlParams("appLanguage")) ||
    _checkLang(utils.getUrlParams("language")) ||
    _checkLang(utils.getUrlParams("lang"));
  if (fromUrl) return fromUrl;

  try {
    const stored = localStorage.getItem(LANG_STORAGE_KEY);
    if (stored) {
      const fromStorage = _checkLang(stored);
      if (fromStorage) return fromStorage;
    }
  } catch (_) {}

  return _getSystemLang() || "en-US";
};

export const curLang = ref(_resolveLang());

export const setLang = (lang) => {
  if (!languageList.includes(lang)) return;
  curLang.value = lang;
  document.body.className = document.body.className
    .replace(/\s*(en-US|id-ID|th-TH|zh-CN)\b/g, " ")
    .trim();
  document.body.className += " " + lang;
  document.documentElement.setAttribute("lang", lang);
  try { localStorage.setItem(LANG_STORAGE_KEY, lang); } catch (_) {}
};

export default () => {
  const getLangTxt = (key, msg) => {
    let info = languageTxt[curLang.value] || languageTxt["en-US"];
    return info[key] || String(msg);
  };

  const getLangCode = (lang) => {
    const langStr = String(lang?.value ?? lang);
    let code = "";
    if (langStr.indexOf("-") > -1) {
      const langArr = langStr.split("-");
      code = langArr[0] === "zh" ? langArr[1] : langArr[0];
    }
    return code.toLowerCase();
  };

  const addMeta = (metaname, content) => {
    const meta = document.createElement("meta");
    meta.content = content;
    meta.name = metaname;
    document.getElementsByTagName("head")[0].appendChild(meta);
  };

  const initLang = () => {
    document.body.className += " " + curLang.value;
    document
      .getElementsByTagName("html")[0]
      .setAttribute("dir", curLang.value == "ar-SA" ? "rtl" : "ltr");
  };

  const langData = new Proxy({}, {
    get(_, key) {
      const info = languageTxt[curLang.value] || languageTxt["en-US"];
      return info[key];
    },
  });

  return {
    curLang,
    setLang,
    languageList,
    initLang,
    getLangTxt,
    getLangCode,
    addMeta,
    langData,
  };
};
